import { useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Metric,
  Select,
  SeparationOfDutiesState,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type {
  AuditChainEntry,
  ContextBridgeRelated,
  CreateObligationScheduleInput,
  DateLifecycleState,
  ObligatedDate,
  ObligationSchedule,
  ReflectedDate,
  ScheduleLifecycle,
  SourceBinding,
  SourceFidelity,
} from "@odingard/sdk-client";
import { ApiError } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, calendarApi } from "../../lib/api";

const FIDELITY_TONE: Record<SourceFidelity, "success" | "warning" | "danger" | "neutral"> = {
  CURRENT: "success",
  STALE: "warning",
  WITHDRAWN: "danger",
  UNBOUND: "neutral",
};
const DATE_TONE: Record<string, "success" | "warning" | "danger" | "neutral"> = {
  SCHEDULED: "neutral",
  DUE: "warning",
  OVERDUE: "danger",
  ACKNOWLEDGED: "success",
  WAIVED: "neutral",
};
const SCHEDULE_TONE: Record<ScheduleLifecycle, "success" | "warning" | "danger" | "neutral"> = {
  DRAFT: "neutral",
  ACTIVE: "success",
  PAUSED: "warning",
  RETIRED: "danger",
};
function personName(
  id: string | null | undefined,
  members: { id: string; displayName: string; email: string }[],
): string {
  if (!id) return "No accountable owner assigned";
  const member = members.find((m) => m.id === id);
  return member ? member.displayName || member.email : "No name available";
}

function errMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.code === "forbidden" && typeof error.details?.permission === "string") {
    return `This action is disabled because you do not hold ${error.details.permission}.`;
  }
  return error instanceof Error ? error.message : fallback;
}

type Refusal = {
  separationOfDuties: boolean;
  message?: string;
  state?: ReactNode;
};

function refusal(
  error: unknown,
  fallback: string,
  members: { id: string; displayName: string; email: string }[],
  action: string,
  reason: string,
): Refusal {
  if (error instanceof ApiError && error.code === "separation_of_duties") {
    const details = error.details as {
      accountableOwnerId?: unknown;
      scheduleCreatorId?: unknown;
      whoCanAct?: unknown;
    } | undefined;
    const ids = [details?.accountableOwnerId, details?.scheduleCreatorId]
      .filter((id): id is string => typeof id === "string");
    const names = [...new Set(ids)].map((id) => personName(id, members));
    const whoCanAct = names.length > 0
      ? typeof details?.whoCanAct === "string"
        ? `${names.join(" and ")} cannot act. ${details.whoCanAct}`
        : `${names.join(" and ")} must act instead.`
      : typeof details?.whoCanAct === "string"
        ? details.whoCanAct
        : undefined;
    return {
      separationOfDuties: true,
      state: <SeparationOfDutiesState action={action} reason={`${reason} No permission change would resolve this business conflict.`} {...(whoCanAct ? { whoCanAct } : {})} />,
    };
  }
  return { separationOfDuties: false, message: errMessage(error, fallback) };
}

function RefusalNotice({ refusal: value }: { refusal: Refusal | null }) {
  if (!value) return null;
  return value.separationOfDuties ? value.state : <p role="alert">{value.message}</p>;
}

function Fidelity({ value }: { value: SourceFidelity }) {
  return <Badge tone={FIDELITY_TONE[value]}>{value}</Badge>;
}

function Lifecycle({ value }: { value: string }) {
  const tone = DATE_TONE[value as DateLifecycleState] ?? SCHEDULE_TONE[value as ScheduleLifecycle] ?? "neutral";
  return <Badge tone={tone}>{value}</Badge>;
}

function DemoNotice({ demo }: { demo: boolean }) {
  return demo ? (
    <div className="ods-failclosed" role="status">
      Demo data is shown because the Calendar AI service is not connected.
    </div>
  ) : null;
}

function sourceReference(binding: SourceBinding | undefined): string {
  if (!binding) return "Source reference unavailable";
  return `${binding.owningApplication} · ${binding.sourceObjectType}/${binding.sourceObjectId} · ${binding.sourceDateField}`;
}

function scheduleLabel(schedule: ObligationSchedule, binding?: SourceBinding): string {
  if (binding) return `${sourceReference(binding)} · ${schedule.offsetDays == null ? "explicit schedule" : `${schedule.offsetDays}-day offset`}`;
  if (schedule.authorityBasis) return `${schedule.authorityBasis} authority · ${schedule.derivationMode}`;
  return `Calendar AI ${schedule.derivationMode} schedule`;
}

function dateLabel(date: ObligatedDate, schedule?: ObligationSchedule, binding?: SourceBinding): string {
  return `${schedule ? scheduleLabel(schedule, binding) : "Calendar AI obligation"} · ${formatDate(date.dueDate)}`;
}

function owningApplicationRoute(application: string): string | null {
  return { vendor_ai: "/studio/vendor-ai" }[application] ?? null;
}

function applicationName(application: string): string {
  return { calendar_ai: "Calendar AI", vendor_ai: "Vendor AI" }[application] ?? application;
}

function ResolutionDestination({ reflected }: { reflected: ReflectedDate }) {
  const route = owningApplicationRoute(reflected.owningApplication);
  return route ? (
    <a href={route}>Open {reflected.owningApplication} in Studio</a>
  ) : reflected.actionEndpoint ? (
    <span>Resolution endpoint: {reflected.actionEndpoint}</span>
  ) : (
    <span>No owning-application Studio destination is registered.</span>
  );
}

function ReflectedDateView({
  reflected,
  audit,
}: {
  reflected: ReflectedDate;
  audit: AuditChainEntry[];
}) {
  return (
    <>
      <Header title={`${reflected.owningApplication} reflected date`} description="A date owned by another application; Calendar AI does not author its lifecycle or response state." back="/studio/calendar-ai/reflected-dates" />
      <div className="ods-failclosed" role="status">
        Resolution owner: {reflected.owningApplication}. <ResolutionDestination reflected={reflected} />
      </div>
      <Card><CardHeader title="Identity" /><CardBody><p>Owning application: {reflected.owningApplication}</p><p>Authoritative date reference: {reflected.authoritativeDateId ?? "UNOBSERVED"}</p></CardBody></Card>
      <Card><CardHeader title="Status" /><CardBody><p>Mirror status: {reflected.mirrorStatus}</p><p>Authoritative state: {reflected.authoritativeState ?? "UNOBSERVED"}</p></CardBody></Card>
      <Card><CardHeader title="Owner" /><CardBody><p>Accountable owner: Not asserted by the owning application.</p></CardBody></Card>
      <Card><CardHeader title="Risk or condition" /><CardBody><p>Source fidelity: <Fidelity value={reflected.sourceFidelity} /></p><p>Source revision: {reflected.sourceRevision || "UNOBSERVED"}</p><p>Last observed: {formatDate(reflected.observedAt)}</p>{reflected.incompleteReason ? <p role="alert">Incomplete provenance: {reflected.incompleteReason}</p> : null}</CardBody></Card>
      <Card><CardHeader title="Relationships" /><CardBody><p>Source binding: {reflected.sourceBindingId}</p><p>Relationship basis: asserted. This reflection is non-authoritative.</p></CardBody></Card>
      <Card><CardHeader title="Timeline" /><CardBody><p>Authoritative date value: {formatDate(reflected.authoritativeDateValue)}</p><p>Observed: {formatDate(reflected.observedAt)}</p></CardBody></Card>
      <Card><CardHeader title="Evidence" /><CardBody><p>Calendar AI stores the source reference, not source evidence.</p></CardBody></Card>
      <Card><CardHeader title="Activity" /><CardBody><p>{audit.some((entry) => entry.subjectId === reflected.id) ? "Activity is retained in the audit chain below." : "No local activity is recorded."}</p></CardBody></Card>
      <Card><CardHeader title="Approvals" /><CardBody><p>Reflected dates have no Calendar AI approvals or governed response actions.</p></CardBody></Card>
      <Card><CardHeader title="Tasks and deadlines" /><CardBody><p>No Calendar AI task or deadline is asserted for this reflected date.</p></CardBody></Card>
      <IntelligenceEmpty />
      <Card><CardHeader title="Audit and history" /><CardBody><p>No local action controls are available.</p></CardBody></Card>
    </>
  );
}

function Header({ title, description, back, action }: { title: string; description: string; back?: string; action?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      <div className="ods-row">
        {action}
        {back ? <Link to={back}><Button size="sm" variant="secondary">Back</Button></Link> : null}
      </div>
    </div>
  );
}

function IntelligenceEmpty() {
  return (
    <Card>
      <CardHeader title="Intelligence" />
      <CardBody>
        <EmptyState title="No derived statements available" description="This object's Intelligence section is empty in Calendar AI R1. The shared Studio Intelligence Panel is pending centrally under ODS-EWO-032 R1." />
      </CardBody>
    </Card>
  );
}

export function CalendarAiLanding() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [params] = useSearchParams();
  const subjectType = params.get("subjectType");
  const subjectId = params.get("subjectId");
  const bridge = useAsync(
    async () => (subjectType && subjectId ? calendarApi.contextBridge(orgId, subjectType, subjectId) : null),
    [orgId, subjectType ?? "", subjectId ?? ""],
  );
  const related = bridge.data?.data.related ?? [];
  return (
    <>
      <Header title="Calendar AI" description="The obligation register: dates the business must meet, why they exist, and what still needs a governed response." />
      <p className="ods-failclosed">The shared Attention Center is pending centrally under ODS-EWO-032 R1. Governed Calendar AI actions resolve in this application.</p>
      {bridge.data?.data.accepted && related.length > 0 ? (
        <Card>
          <CardHeader title={`Dates for ${subjectType}`} />
          <CardBody>
            {related.map((relation: ContextBridgeRelated) => (
              <div key={relation.application}>
                <strong>{applicationName(relation.application)}</strong>
                {relation.count === null ? <p aria-label="Count unavailable">Count unavailable — Calendar AI could not resolve this count.</p> : <p>{relation.count} authored date{relation.count === 1 ? "" : "s"}</p>}
                {relation.dates?.map((date) => <Link key={date.id} to={`/studio/calendar-ai/obligated-dates/${date.id}`}><div>{formatDate(date.dueDate)} · <Lifecycle value={date.lifecycleState} /></div></Link>)}
              </div>
            ))}
          </CardBody>
        </Card>
      ) : null}
      <div className="metric-grid">
        <Link className="metric-card" to="/studio/calendar-ai/obligation-schedules"><Metric value="Schedules" label="Authored obligation schedules" /></Link>
        <Link className="metric-card" to="/studio/calendar-ai/obligated-dates"><Metric value="Dates" label="Authored obligated dates" /></Link>
        <Link className="metric-card" to="/studio/calendar-ai/reflected-dates"><Metric value="Reflected" label="Dates owned elsewhere" /></Link>
      </div>
    </>
  );
}

export function CalendarAiSchedules() {
  const { activeOrg } = useAuth();
  const [params] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(params.get("create") === "1");
  const state = useAsync(async () => {
    const [schedules, bindings, members] = await Promise.all([
      calendarApi.schedules(activeOrg?.id ?? ""),
      calendarApi.sourceBindings(activeOrg?.id ?? ""),
      api.members(activeOrg?.id ?? ""),
    ]);
    return { schedules, bindings, members };
  }, [activeOrg?.id ?? ""]);
  const schedules = state.data?.schedules.data.schedules ?? [];
  const bindings = state.data?.bindings.data.bindings ?? [];
  const members = state.data?.members.members ?? [];
  const [filter, setFilter] = useState<ScheduleLifecycle | "ALL">("ALL");
  const visible = filter === "ALL" ? schedules : schedules.filter((schedule) => schedule.lifecycleState === filter);
  const groups = (["DRAFT", "ACTIVE", "PAUSED", "RETIRED"] as ScheduleLifecycle[])
    .map((lifecycle) => ({ lifecycle, schedules: visible.filter((schedule) => schedule.lifecycleState === lifecycle) }))
    .filter((group) => group.schedules.length > 0);
  return (
    <>
      <Header
        title="Obligation schedules"
        description="Schedules authored by Calendar AI and materialized into dates."
        back="/studio/calendar-ai"
        action={<Button size="sm" onClick={() => setCreateOpen((open) => !open)}>Create obligation schedule</Button>}
      />
      {state.loading && <LoadingState label="Loading obligation schedules…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
      {state.data ? <DemoNotice demo={state.data.schedules.demoMode} /> : null}
      {createOpen ? <ScheduleAuthoring members={members} bindings={bindings} onCreated={() => { setCreateOpen(false); state.reload(); }} /> : null}
      {state.data && schedules.length === 0 ? <EmptyState title="No obligation schedules" description="No schedules have been authored yet." /> : null}
      {schedules.length > 0 ? (
        <>
          <Card><CardBody><Field label="Filter schedules by lifecycle state" help="This filter applies to every group below.">
            {(id) => <Select id={id} value={filter} onChange={(event) => setFilter(event.target.value as ScheduleLifecycle | "ALL")}>
              <option value="ALL">All lifecycle states</option>
              <option value="DRAFT">Draft</option><option value="ACTIVE">Active</option><option value="PAUSED">Paused</option><option value="RETIRED">Retired</option>
            </Select>}
          </Field></CardBody></Card>
          {groups.map((group) => (
            <Card key={group.lifecycle}>
              <CardHeader title={`${group.lifecycle} (${group.schedules.length})`} />
              <CardBody><p>Filtered view count: {group.schedules.length}</p>
                <DataTable caption={`${group.lifecycle} obligation schedules`} rows={group.schedules} columns={[
                  { key: "id", header: "Schedule", render: (s: ObligationSchedule) => <Link to={`/studio/calendar-ai/obligation-schedules/${s.id}`}>{scheduleLabel(s, bindings.find((b) => b.id === s.sourceBindingId))}</Link> },
                  { key: "lifecycleState", header: "State", render: (s) => <Lifecycle value={s.lifecycleState} /> },
                  { key: "timeZone", header: "Time zone", render: (s) => s.timeZone },
                  { key: "accountableOwner", header: "Accountable owner", render: (s) => personName(s.accountableOwner, members) },
                  { key: "actions", header: "Governed actions", render: (s) => <Link to={`/studio/calendar-ai/obligation-schedules/${s.id}`}>Open actions</Link> },
                ]} />
              </CardBody>
            </Card>
          ))}
        </>
      ) : null}
    </>
  );
}

function ScheduleAuthoring({
  members,
  bindings,
  onCreated,
}: {
  members: { id: string; displayName: string; email: string }[];
  bindings: SourceBinding[];
  onCreated: () => void;
}) {
  const { activeOrg } = useAuth();
  const [derivationMode, setDerivationMode] = useState<"EXPLICIT_DATE" | "SOURCE_FIELD" | "SOURCE_OFFSET">("EXPLICIT_DATE");
  const [dueDate, setDueDate] = useState("");
  const [owner, setOwner] = useState("");
  const [timeZone, setTimeZone] = useState("UTC");
  const [noticeOffsets, setNoticeOffsets] = useState("14,3");
  const [consequence, setConsequence] = useState("");
  const [authorityBasis, setAuthorityBasis] = useState("");
  const [bindingId, setBindingId] = useState("");
  const [offsetDays, setOffsetDays] = useState("0");
  const [error, setError] = useState<Refusal | null>(null);
  const [saving, setSaving] = useState(false);
  async function submit() {
    setError(null);
    setSaving(true);
    const input: CreateObligationScheduleInput = {
      derivationMode,
      accountableOwner: owner,
      timeZone,
      noticeOffsetsDays: noticeOffsets.split(",").map((value) => Number(value.trim())).filter((value) => Number.isInteger(value)),
      consequence,
      ...(derivationMode === "EXPLICIT_DATE" ? { explicitDueDate: dueDate, authorityBasis } : { sourceBindingId: bindingId }),
      ...(derivationMode === "SOURCE_OFFSET" ? { offsetDays: Number(offsetDays) } : {}),
    };
    try {
      await calendarApi.createSchedule(activeOrg?.id ?? "", input);
      onCreated();
    } catch (caught) {
      setError(refusal(caught, "Calendar AI refused schedule authoring.", members, "Create this schedule", "This schedule cannot be created by this actor."));
    } finally {
      setSaving(false);
    }
  }
  return <Card><CardHeader title="Author an obligation schedule" /><CardBody>
    <p>Calendar AI owns this schedule and will materialize its dates only after a governed activation.</p>
    <div className="ods-form-grid">
      <Field label="Derivation mode">{(id) => <Select id={id} value={derivationMode} onChange={(event) => setDerivationMode(event.target.value as typeof derivationMode)}>
        <option value="EXPLICIT_DATE">Register-A explicit date</option>
        <option value="SOURCE_FIELD" disabled={bindings.length === 0}>Source field{bindings.length === 0 ? " (no bindings available)" : ""}</option>
        <option value="SOURCE_OFFSET" disabled={bindings.length === 0}>Source offset{bindings.length === 0 ? " (no bindings available)" : ""}</option>
      </Select>}</Field>
      <Field label="Due date" help="Required for Register-A explicit dates.">{(id) => <TextInput id={id} type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} required={derivationMode === "EXPLICIT_DATE"} />}</Field>
      <Field label="Accountable owner">{(id) => <Select id={id} value={owner} onChange={(event) => setOwner(event.target.value)} required>
        <option value="">Choose a member</option>{members.map((member) => <option key={member.id} value={member.id}>{member.displayName || member.email}</option>)}
      </Select>}</Field>
      <Field label="Time zone">{(id) => <TextInput id={id} value={timeZone} onChange={(event) => setTimeZone(event.target.value)} required />}</Field>
      <Field label="Notice offsets (days)" help="Comma-separated whole days.">{(id) => <TextInput id={id} value={noticeOffsets} onChange={(event) => setNoticeOffsets(event.target.value)} />}</Field>
      <Field label="Consequence">{(id) => <TextInput id={id} value={consequence} onChange={(event) => setConsequence(event.target.value)} required />}</Field>
      {derivationMode === "EXPLICIT_DATE" ? <Field label="Authority basis" help="State what actually obliges the business: a statute, regulation, policy, or contractual commitment, in your own words.">{(id) => <TextInput id={id} value={authorityBasis} onChange={(event) => setAuthorityBasis(event.target.value)} required />}</Field> : <Field label="Source binding">{(id) => <Select id={id} value={bindingId} onChange={(event) => setBindingId(event.target.value)} required>
        <option value="">Choose a source binding</option>{bindings.map((binding) => <option key={binding.id} value={binding.id}>{sourceReference(binding)}</option>)}
      </Select>}</Field>}
      {derivationMode === "SOURCE_OFFSET" ? <Field label="Offset days">{(id) => <TextInput id={id} type="number" value={offsetDays} onChange={(event) => setOffsetDays(event.target.value)} required />}</Field> : null}
    </div>
    {derivationMode !== "EXPLICIT_DATE" && bindings.length > 0 ? <p role="status">Source-bound authoring uses the owning application&apos;s authority. If that authority is unavailable, Calendar AI will refuse the action rather than substitute a service identity.</p> : null}
    <RefusalNotice refusal={error} />
    <Button onClick={() => void submit()} disabled={saving || !owner || !consequence || (derivationMode === "EXPLICIT_DATE" ? !dueDate || !authorityBasis : !bindingId)}>{saving ? "Saving…" : "Create draft schedule"}</Button>
  </CardBody></Card>;
}

export function CalendarAiScheduleDetail() {
  const { scheduleId = "" } = useParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [transitionTarget, setTransitionTarget] = useState<ScheduleLifecycle | null>(null);
  const [transitionReason, setTransitionReason] = useState("");
  const [actionError, setActionError] = useState<Refusal | null>(null);
  const state = useAsync(async () => {
    const [scheduleResponse, dates, bindings, members] = await Promise.all([calendarApi.schedule(orgId, scheduleId), calendarApi.dates(orgId), calendarApi.sourceBindings(orgId), api.members(orgId)]);
    return { scheduleResponse, dates, bindings, members };
  }, [orgId, scheduleId]);
  const schedule = state.data?.scheduleResponse.data.schedule;
  const dates = state.data?.dates.data.dates.filter((d) => d.scheduleId === scheduleId) ?? [];
  const binding = state.data?.bindings.data.bindings.find((b) => b.id === schedule?.sourceBindingId);
  const members = state.data?.members.members ?? [];
  async function transition() {
    if (!schedule || !transitionTarget || !transitionReason.trim()) return;
    setActionError(null);
    try {
      await calendarApi.transitionSchedule(orgId, schedule.id, { newState: transitionTarget, reason: transitionReason });
      setTransitionTarget(null);
      setTransitionReason("");
      state.reload();
    } catch (caught) {
      setActionError(refusal(caught, "Calendar AI refused this lifecycle transition.", members, "Transition this schedule", "This schedule transition is refused for separation of duties."));
    }
  }
  if (state.loading && !state.data) return <LoadingState label="Loading obligation schedule…" />;
  if (state.error) return <ErrorState description={state.error.message} onRetry={state.reload} />;
  if (!schedule) return <EmptyState title="Schedule not found" />;
  return (
    <>
      <Header title={scheduleLabel(schedule, binding)} description={schedule.consequence || "An authored Calendar AI obligation schedule."} back="/studio/calendar-ai/obligation-schedules" />
      <Card><CardHeader title="Identity" /><CardBody><p>Schedule reference: {scheduleLabel(schedule, binding)}</p><p>Application owner: Calendar AI</p><p>Support reference: {schedule.id}</p></CardBody></Card>
      <Card><CardHeader title="Status" /><CardBody><Lifecycle value={schedule.lifecycleState} /><p>Derivation: {schedule.derivationMode}</p><p>Time zone: {schedule.timeZone}</p>
        <div className="ods-row">
          {schedule.lifecycleState === "DRAFT" || schedule.lifecycleState === "PAUSED" ? <Button onClick={() => setTransitionTarget("ACTIVE")}>Activate schedule</Button> : null}
          {schedule.lifecycleState === "ACTIVE" ? <Button variant="secondary" onClick={() => setTransitionTarget("PAUSED")}>Pause schedule</Button> : null}
          {schedule.lifecycleState !== "RETIRED" ? <Button variant="danger" onClick={() => setTransitionTarget("RETIRED")}>Retire schedule</Button> : null}
        </div>
        <RefusalNotice refusal={actionError} />
      </CardBody></Card>
      <Card><CardHeader title="Owner" /><CardBody><p>{schedule.accountableOwner ? personName(schedule.accountableOwner, members) : "No accountable owner assigned"}</p></CardBody></Card>
      <Card><CardHeader title="Relationships" /><CardBody><p>Source reference: {sourceReference(binding)}</p><p>Authority basis: {schedule.authorityBasis ?? "INCOMPLETE"}</p></CardBody></Card>
      <Card><CardHeader title="Tasks and deadlines" /><CardBody><DataTable caption="Schedule dates" rows={dates} columns={[
        { key: "id", header: "Date", render: (d: ObligatedDate) => <Link to={`/studio/calendar-ai/obligated-dates/${d.id}`}>{dateLabel(d, schedule, binding)}</Link> },
        { key: "dueDate", header: "Due", render: (d) => formatDate(d.dueDate) },
        { key: "state", header: "State", render: (d) => <Lifecycle value={d.lifecycleState} /> },
      ]} /></CardBody></Card>
      <IntelligenceEmpty />
      <Card><CardHeader title="Audit and history" /><CardBody><p>Schedule changes are retained in Calendar AI&apos;s append-only audit chain.</p></CardBody></Card>
      <ConfirmDialog
        open={transitionTarget !== null}
        title={`Confirm ${transitionTarget?.toLowerCase() ?? "lifecycle"} transition`}
        description="This governed transition is audited and may materialize or change obligated dates."
        confirmLabel={`Confirm ${transitionTarget?.toLowerCase() ?? "transition"}`}
        disabled={!transitionReason.trim()}
        onCancel={() => { setTransitionTarget(null); setTransitionReason(""); }}
        onConfirm={() => void transition()}
      >
        <Field label="Reason" help="A reason is required for every lifecycle transition.">
          {(id) => <TextArea id={id} value={transitionReason} onChange={(event) => setTransitionReason(event.target.value)} required />}
        </Field>
      </ConfirmDialog>
    </>
  );
}

export function CalendarAiDates() {
  const { activeOrg } = useAuth();
  const state = useAsync(() => calendarApi.dates(activeOrg?.id ?? ""), [activeOrg?.id ?? ""]);
  const dates = state.data?.data.dates ?? [];
  const [filter, setFilter] = useState<string>("ALL");
  const visible = filter === "ALL" ? dates : dates.filter((date) => (date.responseState || "UNKNOWN") === filter);
  const groups = (["SCHEDULED", "ACKNOWLEDGED", "WAIVED", "UNKNOWN"] as const)
    .map((responseState) => ({ responseState, dates: visible.filter((date) => (date.responseState || "UNKNOWN") === responseState) }))
    .filter((group) => group.dates.length > 0);
  return (
    <>
      <Header title="Obligated dates" description="Dates Calendar AI authored and governs. Reflected dates are listed separately because they belong to another application." back="/studio/calendar-ai" action={<Link to="/studio/calendar-ai/obligation-schedules?create=1"><Button size="sm">Create obligation schedule</Button></Link>} />
      {state.loading && <LoadingState label="Loading obligated dates…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
      {state.data ? <DemoNotice demo={state.data.demoMode} /> : null}
      {state.data && dates.length === 0 ? <EmptyState title="No obligated dates" description="No Calendar AI-authored dates are visible." /> : null}
      {dates.length > 0 ? <>
        <Card><CardBody><Field label="Filter obligated dates by response state" help="This filter applies to every response-state group below.">
          {(id) => <Select id={id} value={filter} onChange={(event) => setFilter(event.target.value)}>
            <option value="ALL">All response states</option><option value="SCHEDULED">Scheduled</option><option value="ACKNOWLEDGED">Acknowledged</option><option value="WAIVED">Waived</option>{dates.some((date) => !date.responseState) ? <option value="UNKNOWN">Unknown</option> : null}
          </Select>}
        </Field></CardBody></Card>
        {groups.map((group) => <Card key={group.responseState}>
          <CardHeader title={`${group.responseState} (${group.dates.length})`} />
          <CardBody><p>Filtered view count: {group.dates.length}</p><DataTable caption={`${group.responseState} obligated dates`} rows={group.dates} columns={[
            { key: "id", header: "Date", render: (d: ObligatedDate) => <Link to={`/studio/calendar-ai/obligated-dates/${d.id}`}>{dateLabel(d)}</Link> },
            { key: "dueDate", header: "Due", render: (d) => formatDate(d.dueDate) },
            { key: "lifecycleState", header: "State", render: (d) => <Lifecycle value={d.lifecycleState} /> },
            { key: "sourceFidelity", header: "Source fidelity", render: (d) => <Fidelity value={d.sourceFidelity} /> },
            { key: "responseState", header: "Response", render: (d) => d.responseState || "Unknown" },
            { key: "actions", header: "Governed actions", render: (d) => <Link to={`/studio/calendar-ai/obligated-dates/${d.id}`}>Open actions</Link> },
          ]} /></CardBody>
        </Card>)}
      </> : null}
    </>
  );
}

export function CalendarAiDateDetail() {
  const { dateId = "" } = useParams();
  const { activeOrg, user } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [waiving, setWaiving] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [rationale, setRationale] = useState("");
  const [selectedOwner, setSelectedOwner] = useState("");
  const [actionError, setActionError] = useState<Refusal | null>(null);
  const state = useAsync(async () => {
    const [date, schedules, reflected, members, audit, bindings] = await Promise.all([
      calendarApi.date(orgId, dateId).catch(() => null),
      calendarApi.schedules(orgId),
      calendarApi.reflectedDates(orgId),
      api.members(orgId),
      calendarApi.auditChain(orgId),
      calendarApi.sourceBindings(orgId),
    ]);
    return { date, schedules, reflected, members, audit, bindings };
  }, [orgId, dateId]);
  const date = state.data?.date?.data.date;
  const reflected = state.data?.reflected.data.reflectedDates.find((d) => d.id === dateId);
  const schedule = state.data?.schedules.data.schedules.find((s) => s.id === date?.scheduleId);
  const binding = state.data?.bindings.data.bindings.find((b) => b.id === schedule?.sourceBindingId);
  const members = state.data?.members.members ?? [];
  const audit = state.data?.audit.data.entries ?? [];
  const actionableFidelity = date?.sourceFidelity === "CURRENT" || date?.sourceFidelity === "UNBOUND";
  const acknowledgeAllowed = Boolean(date) && actionableFidelity;
  const waiveAllowed = Boolean(date) && actionableFidelity;
  const acknowledgeReason = !actionableFidelity ? `Acknowledgment is unavailable while source fidelity is ${date?.sourceFidelity ?? "unknown"}.` : "";
  const waiveReason = !actionableFidelity ? `Waiver is unavailable while source fidelity is ${date?.sourceFidelity ?? "unknown"}.` : "";
  const reload = state.reload;
  async function acknowledge() {
    if (!date) return;
    setActionError(null);
    try { await calendarApi.acknowledgeDate(orgId, date.id); reload(); } catch (error) {
      setActionError(refusal(error, "Calendar AI refused acknowledgment.", members, "Acknowledge this date", "Only the accountable owner may acknowledge this date."));
    }
  }
  async function waive() {
    if (!date) return;
    setActionError(null);
    try { await calendarApi.waiveDate(orgId, date.id, rationale); setWaiving(false); setRationale(""); reload(); } catch (error) {
      setActionError(refusal(error, "Calendar AI refused waiver.", members, "Waive this date", "The person who authored this schedule cannot also waive its dates."));
    }
  }
  async function assignOwner() {
    if (!date || !selectedOwner) return;
    setActionError(null);
    try {
      await calendarApi.assignDate(orgId, date.id, selectedOwner);
      setAssigning(false);
      setSelectedOwner("");
      reload();
    } catch (error) {
      setActionError(refusal(error, "Calendar AI refused accountable-owner assignment.", members, "Assign this date", "This accountable-owner assignment is refused for separation of duties."));
    }
  }
  if (state.loading && !state.data) return <LoadingState label="Loading obligated date…" />;
  if (state.error) return <ErrorState description={state.error.message} onRetry={reload} />;
  if (!date && !reflected) return <EmptyState title="Date not found" />;
  if (reflected && !date) return <ReflectedDateView reflected={reflected} audit={audit} />;
  if (!date) return <EmptyState title="Date not found" />;
  const displayed = date;
  const evidenceEntry = audit.find((entry) => entry.subjectId === displayed.id && entry.action.includes("acknowledged"));
  const evidenceDocumentId = evidenceEntry && typeof evidenceEntry.materialDetail === "object" && evidenceEntry.materialDetail !== null && "evidenceDocumentId" in evidenceEntry.materialDetail
    ? String((evidenceEntry.materialDetail as { evidenceDocumentId?: unknown }).evidenceDocumentId ?? "")
    : "";
  const acknowledgeReasonId = `date-acknowledge-reason-${displayed.id}`;
  const waiveReasonId = `date-waive-reason-${displayed.id}`;
  return (
    <>
      <Header title={dateLabel(displayed, schedule, binding)} description="A governed date authored by Calendar AI." back="/studio/calendar-ai/obligated-dates" />
      <Card><CardHeader title="Identity" /><CardBody><p>Date reference: {dateLabel(displayed, schedule, binding)}</p><p>Owning application: Calendar AI</p><p>Support reference: {displayed.id}</p></CardBody></Card>
      <Card><CardHeader title="Status" action={<div className="ods-row"><Lifecycle value={displayed.lifecycleState} /><Fidelity value={displayed.sourceFidelity} /></div>} /><CardBody><p>Response: {displayed.responseState}</p>{displayed.sourceFidelity === "STALE" || displayed.sourceFidelity === "WITHDRAWN" ? <p role="alert">This source is {displayed.sourceFidelity.toLowerCase()}; the date is not presented as a live countdown.</p> : <p>Due: {formatDate(displayed.dueDate)}</p>}</CardBody></Card>
      <Card><CardHeader title="Owner" /><CardBody><p>{personName(displayed.accountableOwner, members)}</p></CardBody></Card>
      <Card><CardHeader title="Risk or condition" /><CardBody><p>Source fidelity: <Fidelity value={displayed.sourceFidelity} /></p><p>Source revision: {displayed.sourceRevision ?? "INCOMPLETE — no source revision observed."}</p><p>Last observed: {formatDate(displayed.updatedAt)}</p></CardBody></Card>
      <Card><CardHeader title="Relationships" /><CardBody><p>Schedule: {schedule ? <Link to={`/studio/calendar-ai/obligation-schedules/${schedule.id}`}>{scheduleLabel(schedule, binding)}</Link> : "INCOMPLETE — schedule unavailable"}</p><p>Relationship basis: asserted. Reflections are excluded from Calendar AI counts and work.</p></CardBody></Card>
      <Card><CardHeader title="Timeline" /><CardBody><p>Created {formatDate(displayed.createdAt)}; last observed {formatDate(displayed.updatedAt)}.</p></CardBody></Card>
      <Card><CardHeader title="Evidence" /><CardBody><p>Calendar AI stores the source reference, not the source document. Evidence document: {evidenceDocumentId || "none recorded"}.</p></CardBody></Card>
      <Card><CardHeader title="Activity" /><CardBody><p>Activity is retained in the audit chain below.</p></CardBody></Card>
      <Card><CardHeader title="Approvals" /><CardBody><p>Waiver requires a rationale and a second actor under Calendar AI governance.</p></CardBody></Card>
      <Card><CardHeader title="Tasks and deadlines" action={<div className="ods-row"><Button size="sm" onClick={acknowledge} disabled={!acknowledgeAllowed} aria-describedby={!acknowledgeAllowed ? acknowledgeReasonId : undefined}>Acknowledge</Button><Button size="sm" variant="danger" onClick={() => setWaiving(true)} disabled={!waiveAllowed} aria-describedby={!waiveAllowed ? waiveReasonId : undefined}>Waive</Button></div>} /><CardBody><p>Required permission: governed by Calendar AI.</p><p>Governed owner assignment is separate from routine date information.</p><div className="ods-row"><Select aria-label="New accountable owner" value={selectedOwner} onChange={(event) => setSelectedOwner(event.target.value)}><option value="">Choose an accountable owner</option>{members.map((member) => <option key={member.id} value={member.id}>{member.displayName || member.email}</option>)}</Select><Button size="sm" variant="secondary" onClick={() => setAssigning(true)} disabled={!selectedOwner}>Assign accountable owner</Button></div>{!acknowledgeAllowed ? <p id={acknowledgeReasonId}>{acknowledgeReason}</p> : null}{!waiveAllowed ? <p id={waiveReasonId}>{waiveReason}</p> : null}<RefusalNotice refusal={actionError} /></CardBody></Card>
      <IntelligenceEmpty />
      <Card><CardHeader title="Audit and history" /><CardBody>{audit.filter((entry: AuditChainEntry) => entry.subjectId === displayed.id).length === 0 ? <p>No audit entries are visible.</p> : <ul>{audit.filter((entry: AuditChainEntry) => entry.subjectId === displayed.id).map((entry) => <li key={entry.id}>{entry.action} by {personName(entry.actorId, members)}{entry.createdAt ? ` on ${formatDate(entry.createdAt)}` : ""}</li>)}</ul>}</CardBody></Card>
      <ConfirmDialog open={waiving} title="Waive this obligation" description={`You are acting as ${user?.displayName || "No name available"}. This governed action is audited and requires a rationale.`} confirmLabel="Confirm waiver" destructive disabled={!rationale.trim()} onCancel={() => setWaiving(false)} onConfirm={() => void waive()}><Field label="Rationale" help="Explain why this obligation is being waived.">{(id) => <TextArea id={id} value={rationale} onChange={(event) => setRationale(event.target.value)} required />}</Field></ConfirmDialog>
      <ConfirmDialog open={assigning} title="Assign accountable owner" description={`You are acting as ${user?.displayName || "No name available"}. This governed assignment is audited.`} confirmLabel="Confirm assignment" disabled={!selectedOwner} onCancel={() => setAssigning(false)} onConfirm={() => void assignOwner()}>
        <p>New accountable owner: {personName(selectedOwner, members)}</p>
      </ConfirmDialog>
    </>
  );
}

export function CalendarAiBindings() {
  const { activeOrg } = useAuth();
  const state = useAsync(() => calendarApi.sourceBindings(activeOrg?.id ?? ""), [activeOrg?.id ?? ""]);
  const bindings = state.data?.data.bindings ?? [];
  return <><Header title="Source bindings" description="Caller-scoped references to owning-application records." back="/studio/calendar-ai" />{state.loading && <LoadingState label="Loading source bindings…" />}{state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}{state.data && bindings.length === 0 ? <EmptyState title="No source bindings" description="No source records are bound." /> : null}{bindings.length > 0 ? <Card><CardBody><DataTable caption="Source bindings" rows={bindings} columns={[{ key: "id", header: "Binding", render: (b: SourceBinding) => <Link to={`/studio/calendar-ai/source-bindings/${b.id}/reconcile`}>{b.id}</Link> }, { key: "source", header: "Source", render: (b) => `${b.owningApplication} · ${b.sourceObjectType}/${b.sourceObjectId}` }, { key: "sourceRevision", header: "Revision", render: (b) => b.sourceRevision }, { key: "status", header: "Status", render: (b) => b.status }]} /></CardBody></Card> : null}</>;
}

export function CalendarAiReconcile() {
  const { bindingId = "" } = useParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const state = useAsync(() => calendarApi.sourceBindings(orgId), [orgId]);
  const binding = state.data?.data.bindings.find((b) => b.id === bindingId);
  const [error, setError] = useState<string | null>(null);
  async function reconcile() {
    setError(null);
    try { await calendarApi.reconcileSourceBinding(orgId, bindingId); } catch (e) { setError(errMessage(e, "Calendar AI refused reconciliation.")); }
  }
  if (state.loading) return <LoadingState label="Loading source binding…" />;
  if (state.error) return <ErrorState description={state.error.message} onRetry={state.reload} />;
  if (!binding) return <EmptyState title="Source binding not found" />;
  return <><Header title={`Reconcile ${sourceReference(binding)}`} description="A governed source reread. Calendar AI uses the caller's source authority and never substitutes a service identity." back="/studio/calendar-ai/source-bindings" /><Card><CardHeader title="Identity" /><CardBody><p>{sourceReference(binding)}</p><p>Support reference: {binding.id}</p></CardBody></Card><Card><CardHeader title="Status" /><CardBody><p>Revision {binding.sourceRevision}; observed {formatDate(binding.observedAt)}.</p><Button onClick={() => void reconcile()}>Reconcile binding</Button>{error ? <p role="alert">{error}</p> : null}</CardBody></Card></>;
}

export function CalendarAiReflectedDates() {
  const { activeOrg } = useAuth();
  const state = useAsync(() => calendarApi.reflectedDates(activeOrg?.id ?? ""), [activeOrg?.id ?? ""]);
  const dates = state.data?.data.reflectedDates ?? [];
  return <><Header title="Reflected dates" description="Dates owned by another application. Calendar AI displays them for context but does not count, queue, or mutate them." back="/studio/calendar-ai" />{state.loading && <LoadingState label="Loading reflected dates…" />}{state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}{state.data && dates.length === 0 ? <EmptyState title="No reflected dates" description="Calendar AI is not reflecting any dates for this organization." /> : null}{dates.length > 0 ? <Card><CardBody><DataTable caption="Reflected dates" rows={dates} columns={[{ key: "id", header: "Date", render: (d: ReflectedDate) => <Link to={`/studio/calendar-ai/obligated-dates/${d.id}`}>{`${d.owningApplication} reflected date · ${formatDate(d.authoritativeDateValue)}`}</Link> }, { key: "owningApplication", header: "Owner", render: (d) => d.owningApplication }, { key: "authoritativeDateValue", header: "Authoritative date", render: (d) => formatDate(d.authoritativeDateValue) }, { key: "sourceFidelity", header: "Fidelity", render: (d) => <Fidelity value={d.sourceFidelity} /> }]} /></CardBody></Card> : null}</>;
}
